import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import type { Story } from "../types/story";
import { sound } from "../lib/sound";
import { Charm } from "./Charms";

/**
 * MOMENT 1 — the opening incantation. The single highest-value moment, and the
 * one a prototype usually wastes on a dead spinner. Here there is no spinner:
 * the storybook is WOVEN in front of the child while the story file loads.
 *
 * The cover paints itself in; the child's name writes itself into the dedication
 * in its own warm colour; the things they love drift in like ink settling onto
 * the page; the title settles word by word. "Start reading" wakes once the story
 * is ready, and the soft landing note plays as you cross the threshold (in App's
 * startReading, the user gesture that also unlocks audio on iOS).
 *
 * It reuses the dimensional-book chrome from the cover (spine, gold keyline,
 * embossed seal); this component only adds the WEAVING on top. Every entrance
 * animates from hidden → the element's settled resting style, so under
 * prefers-reduced-motion (animations globally disabled) the cover simply appears
 * already woven — calm, complete, legible.
 */

function resolveSrc(src: string, assetBase: string): string {
  if (/^https?:\/\//.test(src) || src.startsWith("/")) return src;
  return `${assetBase}${src}`;
}

export function Incantation({
  story,
  child,
  assetBase,
  ready,
  progress = 1,
  onStart,
  onBrowse,
}: {
  story: Story | null;
  child: string | undefined;
  /** Prefix for the cover's relative asset path (site root or CDN directory). */
  assetBase: string;
  /** True once the story (and its pre-warmed assets) are ready — Start wakes. */
  ready: boolean;
  /**
   * Asset pre-warm progress 0..1 (see lib/preloadAssets). Drives the golden
   * stitch running around the cover — honest progress, woven into the spell.
   */
  progress?: number;
  onStart: () => void;
  /** When the library exists, a quiet way back to the shelf. */
  onBrowse?: () => void;
}) {
  const cover = story?.cover;
  const name = (child || "").trim() || "you";
  const interests = story?.child?.interests ?? [];
  const pageCount =
    story && Array.isArray(story.canonicalPath) && story.canonicalPath.length > 0
      ? story.canonicalPath.length
      : story?.scenes.length ?? 0;

  return (
    <div className="cover incantation">
      <div className="cover-stage">
        {/* Ambient glow + contact shadow: float the book above the page. */}
        <span className="cover-glow" aria-hidden="true" />
        <span className="cover-contact" aria-hidden="true" />

        <div className="cover-float">
          <span className="cover-pageblock cover-pageblock--back" aria-hidden="true" />
          <span className="cover-pageblock cover-pageblock--mid" aria-hidden="true" />

          {/* The board paints itself in, left to right. */}
          <div className="cover-board incantation-board">
            {cover?.src ? (
              <img
                className="cover-board__face"
                src={resolveSrc(cover.src, assetBase)}
                alt={cover.alt}
                draggable={false}
                decoding="async"
              />
            ) : (
              <span
                className="cover-board__face cover-board__face--placeholder"
                aria-hidden="true"
              />
            )}

            <span className="cover-board__spine" aria-hidden="true" />
            <span className="cover-board__sheen" aria-hidden="true" />
            <span className="cover-board__keyline" aria-hidden="true" />

            {/* The golden stitch: real asset progress sewn around the cover. */}
            <GoldenStitch progress={progress} done={ready} />

            {/* The dedication, with the child's name writing itself in. */}
            <div className="cover-board__seal incantation-seal">
              <SealStar />
              <span>Made for&nbsp;</span>
              <WrittenName name={name} />
            </div>
          </div>
        </div>

        {/* The things they love, drifting in like ink settling on the page. */}
        <Motifs interests={interests} />
      </div>

      <div className="cover-title">
        <h1 className="incantation-title">
          <WrittenTitle title={story?.title} child={child} />
        </h1>
        {story && (
          <p className="cover-subtitle incantation-subtitle">
            <span className="cover-rule" aria-hidden="true" />
            <SpeakerIcon />
            <span>
              Read-along · {pageCount} {pageCount === 1 ? "page" : "pages"}
            </span>
            <span className="cover-rule" aria-hidden="true" />
          </p>
        )}
      </div>

      <button
        type="button"
        className="btn btn--start incantation-start"
        onPointerDown={() => sound.unlock()}
        onClick={onStart}
        disabled={!ready}
      >
        {ready ? "▶ Start reading" : "Weaving your story…"}
      </button>

      {/* While the assets arrive, the weaving narrates itself — softly. */}
      <WeavingVerse weaving={!ready} />

      {onBrowse && (
        <button
          type="button"
          className="incantation-browse"
          onClick={onBrowse}
        >
          <ShelfIcon /> Choose a different story
        </button>
      )}
    </div>
  );
}

/**
 * A hairline of gold thread sewing itself around the cover as the story's
 * assets are pre-warmed (images + the first narration). pathLength=100
 * normalizes the dash math; the honest progress is driven by inline style, not
 * animation, so reduced-motion users still see it fill (transitions off simply
 * make it step). Once done it glints and melts away — its resting style is
 * fully transparent, so it never lingers.
 */
function GoldenStitch({ progress, done }: { progress: number; done: boolean }) {
  const p = Math.min(1, Math.max(0, progress));
  return (
    <svg
      className={done ? "cover-stitch cover-stitch--done" : "cover-stitch"}
      viewBox="0 0 100 180"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <rect
        className="cover-stitch__thread"
        x="3.5"
        y="3.5"
        width="93"
        height="173"
        rx="6"
        pathLength={100}
        style={{ strokeDashoffset: 100 - p * 100 }}
      />
    </svg>
  );
}

/* What the loom is doing, whispered one line at a time. Static, authored text
   only — the loading moment never generates anything. */
const VERSES = [
  "Gathering starlight…",
  "Painting the pictures…",
  "Warming the storyteller's voice…",
  "Pressing gold onto the cover…",
  "Tucking in the page corners…",
];

function WeavingVerse({ weaving }: { weaving: boolean }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!weaving) return;
    const id = window.setInterval(
      () => setI((n) => (n + 1) % VERSES.length),
      2400
    );
    return () => window.clearInterval(id);
  }, [weaving]);
  return (
    <p className="incantation-verse" aria-hidden="true">
      {/* keyed so each verse re-weaves in; empty (height reserved) once ready */}
      {weaving ? <span key={i}>{VERSES[i]}</span> : null}
    </p>
  );
}

/** The child's name, written letter by letter in warm ink. */
function WrittenName({ name }: { name: string }) {
  return (
    <span className="ink-name">
      {[...name].map((ch, i) => (
        <span
          key={i}
          className="ink-letter"
          style={{ animationDelay: `${980 + i * 70}ms` }}
        >
          {ch === " " ? " " : ch}
        </span>
      ))}
    </span>
  );
}

/** The story title, settling in word by word; the child's name glows warm. */
function WrittenTitle({
  title,
  child,
}: {
  title: string | undefined;
  child: string | undefined;
}) {
  if (!title) return null;
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const childN = child ? norm(child) : "";
  const words = title.split(/\s+/).filter(Boolean);
  return (
    <>
      {words.map((w, i) => {
        const isName = childN.length > 0 && norm(w) === childN;
        return (
          <span
            key={i}
            className={isName ? "title-word title-word--name" : "title-word"}
            style={{ animationDelay: `${760 + i * 90}ms` }}
          >
            {w}
            {i < words.length - 1 ? " " : ""}
          </span>
        );
      })}
    </>
  );
}

/** Up to five interest motifs, drifting in from around the book and settling. */
function Motifs({ interests }: { interests: string[] }) {
  const picked = interests.slice(0, SLOTS.length);
  if (picked.length === 0) return null;
  return (
    <div className="incantation-motifs" aria-hidden="true">
      {picked.map((interest, i) => {
        const slot = SLOTS[i];
        const d = 1300 + i * 240;
        const style = {
          ...slot.pos,
          "--drift-x": slot.dx,
          "--drift-y": slot.dy,
          "--drift-r": slot.r,
          // Two delays: the ink-drift entrance, then the idle breath it hands
          // off to once settled (see .incantation-motif's animation list).
          animationDelay: `${d}ms, ${d + 1150}ms`,
        } as CSSProperties;
        return (
          <span key={i} className="incantation-motif" style={style}>
            <Charm interest={interest} />
          </span>
        );
      })}
    </div>
  );
}

interface Slot {
  pos: CSSProperties;
  dx: string;
  dy: string;
  r: string;
}

/* Resting spots around the book, with the direction each motif drifts from.
   Ordered so a single motif lands top-RIGHT (clear of the top-left seal); more
   interests then fill the other corners. */
const SLOTS: Slot[] = [
  { pos: { top: "8%", right: "-9%" }, dx: "30px", dy: "-14px", r: "12deg" },
  { pos: { bottom: "12%", left: "-10%" }, dx: "-30px", dy: "16px", r: "-12deg" },
  { pos: { bottom: "28%", right: "-11%" }, dx: "32px", dy: "12px", r: "10deg" },
  { pos: { top: "40%", left: "-11%" }, dx: "-32px", dy: "-8px", r: "-9deg" },
  { pos: { top: "-3%", left: "30%" }, dx: "0px", dy: "-22px", r: "8deg" },
];

/* ---- glyphs --------------------------------------------------------------- */

/** Filled gold star for the embossed cover seal. */
function SealStar() {
  return (
    <svg className="cover-board__seal-star" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6L12 17l-5.4 2.8 1.2-6L3.3 9.4l6.1-.8L12 3z"
        fill="var(--gold)"
      />
    </svg>
  );
}

/** Small speaker glyph for the read-along subtitle. */
function SpeakerIcon() {
  return (
    <svg className="cover-subtitle__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
      <path
        d="M16 8.5a4 4 0 0 1 0 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** A tiny bookshelf glyph for the quiet way back to the library. */
function ShelfIcon() {
  return (
    <svg
      className="incantation-browse__icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M4 4.5h3.6V20H4zM9.4 4.5H13V20H9.4zM15.2 5.2l3.5-.8 3 14.9-3.5.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}
